import { z } from 'zod'

import {
  createPaginatedResponseSchema,
  createResponseSchema,
} from '../pagination'
import { Task, TaskSchema } from './base'

// Task dependency type enum
export enum DependencyTypeEnum {
  FINISH_TO_START = 'FINISH_TO_START', // Default: B can start when A finishes
  START_TO_START = 'START_TO_START', // B can start when A starts
  FINISH_TO_FINISH = 'FINISH_TO_FINISH', // B can finish when A finishes
  START_TO_FINISH = 'START_TO_FINISH', // B can finish when A starts (rare)
}

// Task dependency schemas
export const TaskDependencySchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  dependentTaskId: z.string().uuid(),
  dependencyTaskId: z.string().uuid(),
  dependencyType: z
    .nativeEnum(DependencyTypeEnum)
    .default(DependencyTypeEnum.FINISH_TO_START),
  lagTime: z.number().default(0), // Lag time in minutes (can be negative for lead time)
  description: z.string().nullable().optional(),
  isActive: z.boolean().default(true),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().optional(),
})

export type TaskDependency = z.infer<typeof TaskDependencySchema>

// Task dependency response schemas using factories
export const TaskDependencyResponseSchema =
  createResponseSchema(TaskDependencySchema)
export const TaskDependenciesResponseSchema =
  createPaginatedResponseSchema(TaskDependencySchema)

export type TaskDependencyResponse = z.infer<
  typeof TaskDependencyResponseSchema
>
export type TaskDependenciesResponse = z.infer<
  typeof TaskDependenciesResponseSchema
>

// Create task dependency schema
export const CreateTaskDependencySchema = TaskDependencySchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

export type CreateTaskDependency = z.infer<typeof CreateTaskDependencySchema>

// Update task dependency schema
export const UpdateTaskDependencySchema = TaskDependencySchema.omit({
  id: true,
  organizationId: true,
  dependentTaskId: true,
  dependencyTaskId: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
}).partial()

export type UpdateTaskDependency = z.infer<typeof UpdateTaskDependencySchema>

// Task with dependencies schema
export const TaskWithDependenciesSchema = TaskSchema.extend({
  dependenciesFrom: z.array(TaskDependencySchema).optional(), // Tasks that this task depends on
  dependenciesTo: z.array(TaskDependencySchema).optional(), // Tasks that depend on this task
  customFields: z.record(z.any()).optional(),
})

export type TaskWithDependencies = z.infer<typeof TaskWithDependenciesSchema>

// Task dependency with related tasks
export const TaskDependencyWithTasksSchema = TaskDependencySchema.extend({
  dependentTask: TaskSchema.optional(),
  dependencyTask: TaskSchema.optional(),
})

export type TaskDependencyWithTasks = z.infer<
  typeof TaskDependencyWithTasksSchema
>

// Task dependency utility functions
export class TaskDependencyUtils {
  /**
   * Check if adding a dependency would create a circular dependency.
   * A circular dependency exists if adding dependentTaskId -> dependencyTaskId
   * would create a cycle. This happens when dependencyTaskId already has a path
   * to dependentTaskId in the existing graph.
   */
  static wouldCreateCircularDependency(
    dependentTaskId: string,
    dependencyTaskId: string,
    existingDependencies: TaskDependency[],
  ): boolean {
    // Direct circular dependency (task depends on itself)
    if (dependentTaskId === dependencyTaskId) return true

    // Build dependency graph from existing dependencies only
    // Map: taskId -> Set of tasks it depends on
    const dependencyMap = new Map<string, Set<string>>()
    for (const dep of existingDependencies) {
      if (!dependencyMap.has(dep.dependentTaskId)) {
        dependencyMap.set(dep.dependentTaskId, new Set())
      }
      dependencyMap.get(dep.dependentTaskId)!.add(dep.dependencyTaskId)
    }

    // Check if dependencyTaskId can reach dependentTaskId through existing edges.
    // If so, adding dependentTaskId -> dependencyTaskId would create a cycle.
    // Use BFS to check reachability.
    const visited = new Set<string>()
    const queue: string[] = [dependencyTaskId]
    visited.add(dependencyTaskId)

    while (queue.length > 0) {
      const current = queue.shift()!

      // If we can reach dependentTaskId from dependencyTaskId, adding the edge creates a cycle
      if (current === dependentTaskId) {
        return true
      }

      // Get all tasks that "current" depends on
      const dependencies = dependencyMap.get(current)
      if (dependencies) {
        for (const depId of dependencies) {
          if (!visited.has(depId)) {
            visited.add(depId)
            queue.push(depId)
          }
        }
      }
    }

    return false
  }

  /**
   * Get all dependencies for a task (transitive closure)
   */
  static getAllDependencies(
    taskId: string,
    dependencies: TaskDependency[],
  ): Set<string> {
    const allDeps = new Set<string>()
    const toProcess = [taskId]

    while (toProcess.length > 0) {
      const currentId = toProcess.pop()!
      const directDeps = dependencies
        .filter(d => d.dependentTaskId === currentId)
        .map(d => d.dependencyTaskId)

      for (const depId of directDeps) {
        if (!allDeps.has(depId)) {
          allDeps.add(depId)
          toProcess.push(depId)
        }
      }
    }

    return allDeps
  }

  /**
   * Get all dependents for a task (tasks that depend on this task)
   */
  static getAllDependents(
    taskId: string,
    dependencies: TaskDependency[],
  ): Set<string> {
    const allDependents = new Set<string>()
    const toProcess = [taskId]

    while (toProcess.length > 0) {
      const currentId = toProcess.pop()!
      const directDependents = dependencies
        .filter(d => d.dependencyTaskId === currentId)
        .map(d => d.dependentTaskId)

      for (const depId of directDependents) {
        if (!allDependents.has(depId)) {
          allDependents.add(depId)
          toProcess.push(depId)
        }
      }
    }

    return allDependents
  }

  /** Longest chain by task count. Durations, lag and calendars are not scheduling inputs here. */
  static calculateLongestDependencyChain(
    tasks: Pick<Task, 'id'>[],
    dependencies: Pick<
      TaskDependency,
      'dependencyTaskId' | 'dependentTaskId'
    >[],
  ): string[] {
    const ids = [...new Set(tasks.map(task => task.id))]
    const children = new Map(ids.map(id => [id, new Set<string>()]))
    const incoming = new Map(ids.map(id => [id, 0]))
    for (const edge of dependencies) {
      const next = children.get(edge.dependencyTaskId)
      if (
        !next ||
        !incoming.has(edge.dependentTaskId) ||
        next.has(edge.dependentTaskId)
      )
        continue
      next.add(edge.dependentTaskId)
      incoming.set(
        edge.dependentTaskId,
        incoming.get(edge.dependentTaskId)! + 1,
      )
    }
    const queue = ids.filter(id => incoming.get(id) === 0)
    const length = new Map(ids.map(id => [id, 1]))
    const previous = new Map<string, string>()
    let longest: string | undefined
    // Topological dynamic programming avoids recursion and repeated path expansion.
    for (const id of queue) {
      if (!longest || length.get(id)! > length.get(longest)!) longest = id
      for (const child of children.get(id)!) {
        if (length.get(id)! + 1 > length.get(child)!) {
          length.set(child, length.get(id)! + 1)
          previous.set(child, id)
        }
        const count = incoming.get(child)! - 1
        incoming.set(child, count)
        if (count === 0) queue.push(child)
      }
    }
    if (queue.length !== ids.length)
      throw new Error('Dependency graph contains a cycle')
    const chain: string[] = []
    while (longest !== undefined) {
      chain.push(longest)
      longest = previous.get(longest)
    }
    return chain.reverse()
  }

  /** @deprecated Use calculateLongestDependencyChain. This is not duration-based critical-path analysis. */
  static calculateCriticalPath(
    tasks: Task[],
    dependencies: TaskDependency[],
  ): string[] {
    return this.calculateLongestDependencyChain(tasks, dependencies)
  }

  /**
   * Check if a task can be started based on its dependencies
   */
  static canTaskStart(
    task: Task,
    dependencies: TaskDependency[],
    allTasks: Map<string, Task>,
  ): boolean {
    const taskDependencies = dependencies.filter(
      d => d.dependentTaskId === task.id,
    )

    for (const dep of taskDependencies) {
      const dependencyTask = allTasks.get(dep.dependencyTaskId)
      if (!dependencyTask) continue

      switch (dep.dependencyType) {
        case DependencyTypeEnum.FINISH_TO_START:
          if (!dependencyTask.completedAt) return false
          break
        case DependencyTypeEnum.START_TO_START:
          if (!dependencyTask.startedAt) return false
          break
        case DependencyTypeEnum.FINISH_TO_FINISH:
          // Can start but can't finish until dependency finishes
          // This check is for finishing, not starting
          break
        case DependencyTypeEnum.START_TO_FINISH:
          // Can finish when dependency starts
          // This is rare and usually for just-in-time scenarios
          break
      }
    }

    return true
  }

  /**
   * Calculate lag time adjusted date
   */
  static calculateLagAdjustedDate(
    baseDate: Date,
    lagTimeMinutes: number,
  ): Date {
    return new Date(baseDate.getTime() + lagTimeMinutes * 60 * 1000)
  }
}
